/**
 * Class EmailValidation, which is used to instantly validate the format of email address
 * user input and check to see if the email address is existing after the email input field
 * loses the focus
 */

define([
    'jquery',
    'mage/validation'
], (
    $
) => {
    class EmailValidation {

        /**
         * Constructor
         *
         * @param {Object}  config Configuration options passed in through JSON
         * @param  {string}  config.emailNotAvailableMessageSelector DOM selector for the email not available message container
         * @param {Element} element The element that this class was bound to
         *
         * @returns {null}
         */
        constructor(config, element) {
            const defaultConfig = {
                emailNotAvailableMessageSelector: '[data-js=email-not-available-message]',
                checkEmailAvailability: true
            };
            // Merge default config options and provided config
            this.config = { ...defaultConfig, ...config };

            this.$element = $(element);
            this.$emailNotAvailableMessage = $(this.config.emailNotAvailableMessageSelector);

            this.attachListeners();
        }

        /**
         * Attach event listeners to elements
         *
         * @returns {null}
         */
        attachListeners() {
            /**
             * Bind `blur` listener to element
             *
             * @returns {null}
             */
            this.$element.on('blur', this.handleBlur);
        }

        /**
         * `blur` event handler to validate email format when there is a value provided
         * if the provided email is valid, we check email availability, otherwise, leave
         * validation message as is and hide the email not available message, this is the
         * same validation behaviour with email input in customer login form of the
         * Magento2 one page checkout
         */
        handleBlur = () => {
            if (this.$element.val().trim() === '') {
                return;
            }

            if (this.validateEmailFormat() && this.config.checkEmailAvailability) {
                this.checkEmailAvailability();
            } else {
                this.showEmailNotAvailableMessage(false);
            }
        };

        /**
         * Validate the format of input email to see if it's valid
         *
         * @returns {Boolean}
         */
        validateEmailFormat = () => {
            return $.validator.validateSingleElement(this.$element);
        };

        /**
         * Check to see if the email is available to be used to register a new account,
         *
         * @returns {null}
         */
        checkEmailAvailability = () => {
            // Cancel any pending request that is now out of date
            if (this.checkEmailRequest != null && $.inArray(this.checkEmailRequest.readyState, [1, 2, 3])) {
                this.checkEmailRequest.abort();
                this.checkEmailRequest = null;
            }

            this.checkEmailRequest = $.ajax({
                url: '/rest/V1/customers/isEmailAvailable',
                type: 'POST',
                contentType: 'application/json',
                data: JSON.stringify({ customerEmail: this.$element.val() }),
                dataType: 'json'
            }).done((isEmailAvailable) => {
                this.showEmailNotAvailableMessage(!isEmailAvailable);
                this.toggleEmailValidationErrorClass(!isEmailAvailable);
            }).fail(() => {
                this.showEmailNotAvailableMessage(false);
                this.toggleEmailValidationErrorClass(false);
            });
        };

        /**
         * Toggle to show the email not available message or not, in the meantime,
         * toggle class to email input element
         *
         * @param show {Boolean} determine whether or not to display the element
         */
        showEmailNotAvailableMessage = (show) => {
            this.$emailNotAvailableMessage.toggle(show);
        };

        /**
         * Toggle error class to email input element
         *
         * @param state {Boolean} determine whether add or remove error class
         */
        toggleEmailValidationErrorClass = (state) => {
            this.$element.toggleClass('mage-error', state);
        };

    }

    return (config, element) => {
        const emailValidation = new EmailValidation(config, element);
        return emailValidation;
    };
});
